import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Box,
  Paper,
  Typography,
} from "@mui/material";
import { AttachFile, Cancel as CancelIcon, PictureAsPdf } from "@mui/icons-material";
import FileDropzone from "components/Dropzone";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FlexWrapper from "components/Wrapper/FlexWrapper";

const FormUploadModal = ({
  open,
  onClose,
  onUpload,
  loading,
  formName,
  setFormName,
  visibility,
  setVisibility,
  onboarding,
  setOnboarding,
  minOnboardingStage,
  setMinOnboardingStage,
  required,
  setRequired,
  shortName,
  setShortName,
  status,
  setStatus,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState(null);

  const handleFileUpload = (acceptedFiles, rejectedFiles) => {
    setUploadedFiles(acceptedFiles);
    onUpload(acceptedFiles, rejectedFiles);

    // If shortName is empty and there are files uploaded, set shortName to filename without extension
    if (acceptedFiles && acceptedFiles.length > 0 && !shortName.trim()) {
      const file = acceptedFiles[0];
      // Get filename without extension
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      setShortName(fileNameWithoutExtension);
    }
  };

  const handleProcessForm = () => {
    if (!uploadedFiles || !formName.trim() || !shortName.trim()) return;

    // Close the modal and signal to process the form (parent component has the metadata)
    onClose(true);
  };

  // Handle modal close and reset file state
  const handleClose = (shouldProcess = false) => {
    // Clear uploaded files when modal is closed
    setUploadedFiles(null);

    // Call the parent's onClose function
    onClose(shouldProcess);
  };

  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="md">
      <DialogContent sx={{ padding: "24px", maxWidth: "580px", width: "500px" }}>
        <FlexWrapper justifyContent="space-between" sx={{ flexWrap: "nowrap" }}>
          <MDTypography sx={{ flex: 1 }}>
            <AttachFile color="primary" /> Upload Form Document
          </MDTypography>
          <MDBox sx={{ flex: 0 }}>
            <Button
              variant="text"
              disabled={loading}
              startIcon={<CancelIcon />}
              onClick={() => handleClose(false)}
            >
              Cancel
            </Button>
          </MDBox>
        </FlexWrapper>

        <MDBox mt={3} mb={2}>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Form Name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            disabled={loading}
            placeholder="Enter a name for this form"
            helperText="Please provide a name to help identify this form later"
          />
        </MDBox>

        <Divider sx={{ my: 2 }} />

        {/* Form Metadata Section */}
        <MDBox mt={2} mb={2}>
          <MDTypography variant="h5" color="dark" mb={2}>
            Form Metadata
          </MDTypography>

          {/* Short Name Field */}
          <MDBox mt={2} mb={2}>
            <TextField
              fullWidth
              margin="dense"
              label="Short Name"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              disabled={loading}
              placeholder="Enter a short name for the onboarding button"
              helperText="Short name can't be upadted afterwards, this will used to save data"
              required
              error={shortName.trim() === ""}
            />
          </MDBox>

          {/* Form Visibility */}
          <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend">1. Form Visibility</FormLabel>
            <RadioGroup
              row
              name="visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
            >
              <FormControlLabel value="AdminsOnly" control={<Radio />} label="Admins Only" />
              <FormControlLabel
                value="Both"
                control={<Radio />}
                label="Admins and Applicants Both"
              />
            </RadioGroup>
          </FormControl>

          {/* Onboarding Option */}
          <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend">2. Onboarding</FormLabel>
            <RadioGroup
              row
              name="onboarding"
              value={onboarding}
              onChange={(e) => setOnboarding(e.target.value)}
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          {/* Minimum Onboarding Stage - Only show when Onboarding is Yes */}
          {onboarding === "Yes" && (
            <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
              <FormLabel component="legend">2.1 Minimum Onboarding Stage</FormLabel>
              <RadioGroup
                row
                name="minOnboardingStage"
                value={minOnboardingStage}
                onChange={(e) => setMinOnboardingStage(e.target.value)}
              >
                <FormControlLabel value="New" control={<Radio />} label="New" />
                <FormControlLabel value="ATC" control={<Radio />} label="ATC" />
                <FormControlLabel value="Screened" control={<Radio />} label="Screened" />
                <FormControlLabel value="Pre-Hire" control={<Radio />} label="Pre-Hire" />
              </RadioGroup>
            </FormControl>
          )}

          {/* Required Field - Only show when Onboarding is Yes */}
          {onboarding === "Yes" && (
            <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
              <FormLabel component="legend">2.2 Required at Onboarding</FormLabel>
              <RadioGroup
                row
                name="required"
                value={required}
                onChange={(e) => setRequired(e.target.value)}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          )}

          {/* Active/Inactive Status */}
          <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend">3. Status</FormLabel>
            <RadioGroup
              row
              name="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <FormControlLabel value="Active" control={<Radio />} label="Active" />
              <FormControlLabel value="Inactive" control={<Radio />} label="Inactive" />
            </RadioGroup>
          </FormControl>
        </MDBox>

        <Divider sx={{ my: 2 }} />

        <MDBox mt={2}>
          <MDTypography variant="h5" color="info">
            Upload a form document to be processed
          </MDTypography>
          <FileDropzone
            disabled={loading}
            multiple={false}
            message="Drop your form document here"
            message2="Supported formats: PDF, JPG, PNG"
            onDrop={handleFileUpload}
            accept={{
              "image/*": [".jpeg", ".jpg", ".png"],
              "application/pdf": [".pdf"],
            }}
            sx={{ minHeight: "200px" }}
          />

          {/* Display selected file */}
          {uploadedFiles && uploadedFiles.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mt: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#f5f5f5'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PictureAsPdf color="error" sx={{ mr: 1, fontSize: 28 }} />
                <Box sx={{ ml: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Selected File:
                  </Typography>
                  <Typography variant="body2">
                    {uploadedFiles[0].name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(uploadedFiles[0].size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={() => handleClose(false)}>Cancel</Button>
        <Button
          onClick={handleProcessForm}
          variant="contained"
          color="primary"
          disabled={loading || !uploadedFiles || !formName.trim() || !shortName.trim()}
        >
          Process Form
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FormUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  formName: PropTypes.string.isRequired,
  setFormName: PropTypes.func.isRequired,
  visibility: PropTypes.string.isRequired,
  setVisibility: PropTypes.func.isRequired,
  onboarding: PropTypes.string.isRequired,
  setOnboarding: PropTypes.func.isRequired,
  minOnboardingStage: PropTypes.string.isRequired,
  setMinOnboardingStage: PropTypes.func.isRequired,
  required: PropTypes.string.isRequired,
  setRequired: PropTypes.func.isRequired,
  shortName: PropTypes.string.isRequired,
  setShortName: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  setStatus: PropTypes.func.isRequired,
};

FormUploadModal.defaultProps = {
  loading: false,
};

export default FormUploadModal;
